import { useIsVerticalApp } from '@common/VerticalApps/utils';
import { FROZEN_EMPTY_ARRAY } from '@prophecy/utils/constants';

import { useGraphQlQuery, useGraphQlQueryWithoutShareTokens } from '../../data/util';
import { isReplayMode, replayToken } from '../../utils/replayToken';
import { isShareMode, shareToken } from '../../utils/shareToken';
import { getOnBoardingDetailsReplayQuery, getProjectReplaysQuery } from './queries';

export const REPLAY_QUERY_KEY = 'Replay_Builder';

export const REPLAY_SENTRY_TAG = 'replay.sentry';

export const useOnboardingDetailReplayQuery = ({
  onError,
  enabled = true
}: { enabled?: boolean; onError?: (error: unknown) => void } = {}) => {
  const isVerticalApp = useIsVerticalApp();
  const hasReplayShareMode = isShareMode() || isReplayMode();
  const isGuestUserLoggedIn = Boolean(shareToken.loggedInUserId || replayToken.loggedInUserID);

  const isEnabled = hasReplayShareMode ? isVerticalApp && isGuestUserLoggedIn && enabled : isVerticalApp && enabled;

  // called for normal users or logged-in user in share link
  const { data, isInitialLoading: isOnboardingDetailsLoading } = useGraphQlQueryWithoutShareTokens(
    [REPLAY_QUERY_KEY, 'onboardingReplayQuery'],
    getOnBoardingDetailsReplayQuery,
    {
      enabled: isEnabled,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      onError: onError
    }
  );
  const onboardingReplayDetails = data?.professionalOnboardingDetails ?? [];
  const defaultOnboardingProjectId = onboardingReplayDetails.find((detail) => detail.projectId)?.projectId;

  return {
    onboardingReplayDetails: onboardingReplayDetails,
    defaultOnboardingProjectId,
    defaultReplayUrl: onboardingReplayDetails?.[0]?.replay?.shareLink,
    defaultReplayId: onboardingReplayDetails?.[0]?.replay?.replayId,
    isOnboardingDetailsLoading
  };
};

export const useGetProjectReplaysQuery = ({
  projectUID,
  enabled = true
}: {
  projectUID: string;
  enabled?: boolean;
}) => {
  const isVerticalApp = useIsVerticalApp();
  const { data, isInitialLoading: isProjectReplaysLoading } = useGraphQlQuery(
    [REPLAY_QUERY_KEY, 'ProjectReplaysQuery'],
    getProjectReplaysQuery,
    {
      enabled: isVerticalApp && Boolean(projectUID) && enabled,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    },
    { projectUID }
  );

  return {
    projectReplays: data?.getProjectReplays || (FROZEN_EMPTY_ARRAY as NonNullable<typeof data>['getProjectReplays']),
    isProjectReplaysLoading
  };
};
